function toFiniteNumber(value) {
    const number = Number(value);

    return Number.isFinite(number) ? number : null;
}

function formatNumber(value, fractionDigits = 0) {
    const number = toFiniteNumber(value);

    if (number === null) {
        return '';
    }
    if (Number.isInteger(number)) {
        return String(number);
    }
    return number.toFixed(fractionDigits).replace(/\.0+$/, '').replace(/(\.\d*[1-9])0+$/, '$1');
}

export function escapeHTML(value) {
    return String(value ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}

export function formatTyphoonDateTime(value, withSeconds = true) {
    if (!value) {
        return '';
    }

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
        return String(value);
    }

    const year = date.getFullYear(),
        month = `0${date.getMonth() + 1}`.slice(-2),
        day = `0${date.getDate()}`.slice(-2),
        hours = `0${date.getHours()}`.slice(-2),
        minutes = `0${date.getMinutes()}`.slice(-2),
        seconds = `0${date.getSeconds()}`.slice(-2);

    return withSeconds ?
        `${year}/${month}/${day} ${hours}:${minutes}:${seconds}` :
        `${year}/${month}/${day} ${hours}:${minutes}`;
}

export function formatTyphoonForecastLabel(value, isCurrent = false) {
    if (isCurrent) {
        return '現在';
    }
    if (!value) {
        return '予報';
    }

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
        return String(value);
    }

    return `${date.getMonth() + 1}/${date.getDate()} ${date.getHours()}時`;
}

export function getTyphoonDisplayNumber(value) {
    const digits = String(value ?? '').replace(/\D/g, '');

    if (!digits) {
        return '';
    }
    return String(parseInt(digits.length > 2 ? digits.slice(-2) : digits, 10));
}

export function getTyphoonDisplayTitle(typhoon) {
    const displayNumber = String(
            typhoon?.displayNumber ||
            getTyphoonDisplayNumber(typhoon?.typhoon_name_number || typhoon?.typhoonNameNumber)
        ).trim();

    if (displayNumber) {
        return `台風${displayNumber}号`;
    }
    return '台風情報';
}

export function buildTyphoonAreaIntensitySummary(typhoon) {
    const source = typhoon?.current || typhoon,
        parts = [
            source?.area_class || source?.areaClass || '',
            source?.intensity_class || source?.intensityClass || ''
        ].filter(Boolean);

    return parts.length ? parts.join(' / ') : '-';
}

export function buildTyphoonMovementSummary(typhoon) {
    const source = typhoon?.current || typhoon,
        direction = source?.center_direction || source?.centerDirection || '',
        speed = formatNumber(source?.center_speed_kmh ?? source?.centerSpeedKmh, 1);

    if (direction && speed) {
        return `${direction} ${speed}km/h`;
    }
    if (direction) {
        return direction;
    }
    if (speed) {
        return `${speed}km/h`;
    }
    return '-';
}

export function buildTyphoonWindSummary(typhoon) {
    const source = typhoon?.current || typhoon,
        maxWind = formatNumber(source?.max_wind_speed_ms ?? source?.maxWindSpeedMs, 1),
        maxInstantaneousWind = formatNumber(
            source?.max_instantaneous_wind_speed_ms ?? source?.maxInstantaneousWindSpeedMs,
            1
        );

    return [
        maxWind ? `${maxWind}m/s` : '',
        maxInstantaneousWind ? `${maxInstantaneousWind}m/s` : ''
    ].filter(Boolean).join(' / ') || '-';
}

export function buildTyphoonButtonSummary(typhoon) {
    const source = typhoon?.current || typhoon,
        pressure = formatTyphoonPressure(source?.center_pressure ?? source?.centerPressure),
        windSummary = buildTyphoonWindSummary(typhoon);

    return [
        buildTyphoonAreaIntensitySummary(typhoon),
        buildTyphoonMovementSummary(typhoon),
        [pressure, windSummary !== '-' ? windSummary : ''].filter(Boolean).join(' / ')
    ].filter(Boolean).join(' | ');
}

export function formatTyphoonRadius(value) {
    const formatted = formatNumber(value, 1);

    return formatted ? `${formatted}km` : '-';
}

export function formatTyphoonPressure(value) {
    const formatted = formatNumber(value, 1);

    return formatted ? `${formatted}hPa` : '-';
}

export function formatTyphoonWindSpeed(value) {
    const formatted = formatNumber(value, 1);

    return formatted ? `${formatted}m/s` : '-';
}

export function formatMultilineTyphoonText(value) {
    const escaped = escapeHTML(value || '-');

    return escaped.replace(/\r?\n/g, '<br>');
}
